// Google Tag Manager & Analytics utility module

// Initialize GTM
export const initGTM = (gtmId) => {
  if (typeof window !== 'undefined' && gtmId) {
    window.dataLayer = window.dataLayer || [];

    // GTM Script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

    // Push GTM start event
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    document.head.insertBefore(script, document.head.firstChild);

    // Add noscript iframe to body
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);

    if (document.body) {
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }
};

// Initialize Microsoft Clarity
export const initClarity = (clarityId) => {
  if (typeof window !== 'undefined' && clarityId) {
    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", clarityId);
  }
};

// Initialize all analytics
export const initAnalytics = () => {
  const gtmId = process.env.REACT_APP_GTM_ID;
  const clarityId = process.env.REACT_APP_CLARITY_ID;

  if (gtmId) {
    initGTM(gtmId);
  }

  if (clarityId) {
    initClarity(clarityId);
  }
};

// Push events to dataLayer (GTM will forward to GA4)
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams
    });
  }
};

// ============================================
// User Journey Event Tracking Functions
// ============================================

// Registration Events
export const trackRegistrationStarted = () => {
  trackEvent('registration_started');
};

export const trackRegistrationCompleted = (userId, email) => {
  trackEvent('registration_completed', {
    user_id: userId,
    user_email: email
  });
};

// Berth Listing Events
export const trackBerthListingStarted = () => {
  trackEvent('berth_listing_started');
};

export const trackBerthListingCreated = (berthId, berthType, location) => {
  trackEvent('berth_listing_created', {
    berth_id: berthId,
    berth_type: berthType, // 'hire', 'swap', 'sale'
    berth_location: location
  });
};

// Rental/Swap/Sale Request Events
export const trackRequestStarted = (berthId, requestType) => {
  trackEvent('request_started', {
    berth_id: berthId,
    request_type: requestType // 'hire', 'swap', 'sale'
  });
};

export const trackRequestSubmitted = (berthId, requestType, transactionId) => {
  trackEvent('request_submitted', {
    berth_id: berthId,
    request_type: requestType,
    transaction_id: transactionId
  });
};

// Checkout Events
export const trackCheckoutStarted = (transactionId, amount, transactionType) => {
  trackEvent('checkout_started', {
    transaction_id: transactionId,
    value: amount,
    transaction_type: transactionType
  });
};

export const trackCheckoutCompleted = (transactionId, amount, transactionType) => {
  trackEvent('checkout_completed', {
    transaction_id: transactionId,
    value: amount,
    transaction_type: transactionType
  });
};

// Payment Events
export const trackPaymentStarted = (transactionId, amount) => {
  trackEvent('payment_started', {
    transaction_id: transactionId,
    value: amount
  });
};

export const trackPaymentSuccessful = (transactionId, amount) => {
  trackEvent('payment_successful', {
    transaction_id: transactionId,
    value: amount
  });
};

export const trackPaymentFailed = (transactionId, amount, errorReason) => {
  trackEvent('payment_failed', {
    transaction_id: transactionId,
    value: amount,
    error_reason: errorReason
  });
};

// Page View Tracking
export const trackPageView = (pageName, pageUrl) => {
  trackEvent('page_view', {
    page_name: pageName,
    page_url: pageUrl || window.location.pathname
  });
};
